import { EmptyState } from '@app/components/base/EmptyState';
import { webApi } from '@mi-front/api-clients';
import { Box, Grid } from '@mui/material';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { useState } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { useFragment } from '../../hooks/useFragment';
import { useFragmentSocialUploadsQuery } from '../social-export-panorama/hooks/useFragmentSocialUploadsQuery';
import { CloneVideoEditionProjectDialog } from './CloneVideoEditionProjectDialog';
import { CreateVideoEditionProjectButton } from './CreateVideoEditionProjectButton';
import { ProjectCard } from './ProjectCard';

const videoEditionsEmptyHelpMessage = defineMessage({
  id: 'fragments.videoEditions.emptyHelp',
  defaultMessage: 'No hay proyectos de edición aún',
});

/**
 * Tab content for video editions: list of video editor projects linked to the fragment.
 * Shows each project in an MUI Card with create, clone, delete, open-in-editor, and play-generated actions.
 */
export function FragmentVideoEditionsTab() {
  const { fragment } = useFragment();
  const [cloneProject, setCloneProject] =
    useState<webApi.EnrichedVideoEditorProjectListItemDto | null>(null);
  const uploadsQuery = useFragmentSocialUploadsQuery(fragment.id);

  const listQuery = webApi.useListProjectsVideoEditorProjects({ fragmentId: fragment.id });

  const projects = listQuery.data;
  const showCreateInHeader = projects === undefined || projects.length > 0;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        {showCreateInHeader && (
          <CreateVideoEditionProjectButton
            fragmentId={fragment.id}
            currentProjectCount={projects?.length ?? 0}
          />
        )}
      </Box>

      <QueryView query={listQuery}>
        {(projects) => {
          if (!projects.length) {
            return (
              <EmptyState
                centered
                message={false}
                help={<FormattedMessage {...videoEditionsEmptyHelpMessage} />}
                action={
                  <CreateVideoEditionProjectButton
                    fragmentId={fragment.id}
                    currentProjectCount={0}
                  />
                }
              />
            );
          }

          return (
            <Grid container spacing={2}>
              {projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  uploadsByPlatform={uploadsQuery.data}
                  onClone={() => setCloneProject(project)}
                />
              ))}
            </Grid>
          );
        }}
      </QueryView>

      {cloneProject && (
        <CloneVideoEditionProjectDialog
          open={!!cloneProject}
          onClose={() => setCloneProject(null)}
          sourceProject={cloneProject}
        />
      )}
    </>
  );
}
